import { useEffect, useRef, useState } from "react";
import { saveErrorText } from "../utils/saveErrorText.js";

export default function useBookSync(bookId) {
  const store = useRef(new Map());
  const current = useRef(bookId);
  current.current = bookId;
  const syncFailedRef = useRef(false);
  const [, redraw] = useState(0);
  const [saveErrorOpen, setSaveErrorOpen] = useState(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const entry = (id) => {
    if (!store.current.has(id)) store.current.set(id, { pending: 0, errors: new Map(), sequence: new Map() });
    return store.current.get(id);
  };
  const update = (id) => {
    if (id !== current.current || !mounted.current) return;
    syncFailedRef.current = entry(id).errors.size > 0;
    redraw(n => n + 1);
  };
  const state = entry(bookId);
  syncFailedRef.current = state.errors.size > 0;
  const errors = [...state.errors.values()];
  const saveErrorInfo = errors.at(-1) || null;
  useEffect(() => { setSaveErrorOpen(false); }, [bookId]);

  const markSyncFailed = (what, error, { key = what, targetBookId = bookId } = {}) => {
    const s = entry(targetBookId);
    const info = { what: what || "изменение книги", message: saveErrorText(error) };
    const previous = s.errors.get(key);
    s.errors.set(key, info);
    update(targetBookId);
    if (targetBookId === current.current && mounted.current &&
        (!previous || previous.message !== info.message)) setSaveErrorOpen(true);
  };
  const runSync = async (fn, { what = "изменение книги", key = what,
                              silent = false, targetBookId = bookId } = {}) => {
    const s = entry(targetBookId);
    const sequence = (s.sequence.get(key) || 0) + 1;
    s.sequence.set(key, sequence);
    s.pending += 1;
    update(targetBookId);
    try {
      const result = await fn();
      if (s.sequence.get(key) === sequence) s.errors.delete(key);
      return result;
    } catch (error) {
      if (!silent && s.sequence.get(key) === sequence) {
        markSyncFailed(what, error, { key, targetBookId });
      }
      throw error;
    } finally {
      s.pending = Math.max(0, s.pending - 1);
      update(targetBookId);
    }
  };
  // После успешной загрузки серверной версии старое локальное состояние заменено.
  const resetSyncForBook = (id) => {
    entry(id).errors.clear();
    update(id);
    if (id === current.current) setSaveErrorOpen(false);
  };
  return {
    syncFailedRef, hasSyncError: errors.length > 0, savingCount: state.pending,
    saveErrorInfo, saveErrors: errors, saveErrorOpen: saveErrorOpen && errors.length > 0,
    showSaveError: () => setSaveErrorOpen(true),
    closeSaveError: () => setSaveErrorOpen(false),
    markSyncFailed, runSync, resetSyncForBook,
    getSyncState: () => ({ pending: entry(current.current).pending, failed: entry(current.current).errors.size > 0 }),
  };
}